import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Typography, Tag, Table, Spin, Button, Select, DatePicker, Space, Empty } from 'antd';
import { ReloadOutlined, BarChartOutlined, ClearOutlined } from '@ant-design/icons';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getEventStats } from '../apiCalls';

const { RangePicker } = DatePicker;

const formatDateLabel = (label, groupBy) => {
    if (!label) return '—';
    if (groupBy === 'hour') {
        const d = dayjs(label);
        return d.isValid() ? d.format('DD MMM HH:mm') : label;
    }
    const d = dayjs(label);
    return d.isValid() ? d.format('DD MMM YYYY') : label;
};

/**
 * Super-admin-only panel for browsing aggregated `events` collection data.
 * Supports filtering by event name, propertyID and date range, and grouping
 * the time series by day or hour.
 */
const EventStatsPanel = ({ propList = [] }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const [eventNames, setEventNames] = useState([]);
    const [propertyID, setPropertyID] = useState(undefined);
    const [url, setUrl] = useState(undefined);
    const [range, setRange] = useState([dayjs().subtract(30, 'day'), dayjs()]);
    const [groupBy, setGroupBy] = useState('day');

    const propertyLabelMap = useMemo(() => {
        const map = new Map();
        propList.forEach(p => map.set(p.propertyID, p.companyName));
        return map;
    }, [propList]);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const [startDate, endDate] = range || [];
            const result = await getEventStats({
                eventNames,
                propertyID,
                url,
                startDate: startDate ? startDate.toISOString() : undefined,
                endDate: endDate ? endDate.toISOString() : undefined,
                groupBy,
            });
            setData(result);
        } catch (err) {
            enqueueSnackbar(t('eventStats.error'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    }, [eventNames, propertyID, url, range, groupBy, enqueueSnackbar, t]);

    useEffect(() => { fetchStats(); }, [fetchStats]);

    const eventOptions = useMemo(() => {
        const names = data?.availableEventNames || [];
        return names.map(n => ({ value: n, label: n }));
    }, [data]);

    const propertyOptions = useMemo(() => {
        const ids = data?.availableProperties || [];
        return ids.map(id => ({
            value: id,
            label: propertyLabelMap.get(id) ? `${propertyLabelMap.get(id)} (${id})` : id,
        }));
    }, [data, propertyLabelMap]);

    const urlOptions = useMemo(() => {
        const urls = data?.availableUrls || [];
        return urls.map(u => ({ value: u, label: u }));
    }, [data]);

    const maxTimeSeries = useMemo(() => {
        if (!data?.timeSeries?.length) return 0;
        return Math.max(...data.timeSeries.map(t => t.count));
    }, [data]);

    const breakdownColumns = [
        {
            title: t('eventStats.columns.name'),
            dataIndex: '_id',
            render: (v) => <Typography.Text code>{v}</Typography.Text>,
        },
        {
            title: t('eventStats.columns.count'),
            dataIndex: 'count',
            width: 120,
            align: 'right',
            sorter: (a, b) => a.count - b.count,
            defaultSortOrder: 'descend',
            render: (v) => <Tag color="blue">{v}</Tag>,
        },
    ];

    const propertyColumns = [
        {
            title: t('eventStats.columns.property'),
            dataIndex: '_id',
            render: (v) => {
                if (!v || v === '(none)') {
                    return <Typography.Text type="secondary">{t('eventStats.noProperty')}</Typography.Text>;
                }
                const label = propertyLabelMap.get(v);
                return label ? `${label} (${v})` : v;
            },
        },
        {
            title: t('eventStats.columns.count'),
            dataIndex: 'count',
            width: 120,
            align: 'right',
            sorter: (a, b) => a.count - b.count,
            defaultSortOrder: 'descend',
            render: (v) => <Tag color="geekblue">{v}</Tag>,
        },
    ];

    const urlColumns = [
        {
            title: t('eventStats.columns.url'),
            dataIndex: '_id',
            ellipsis: true,
            render: (v) => {
                if (!v || v === '(none)') {
                    return <Typography.Text type="secondary">{t('eventStats.noUrl')}</Typography.Text>;
                }
                return (
                    <Typography.Text
                        copyable={{ text: v }}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setUrl(v)}
                        title={v}
                    >
                        {v}
                    </Typography.Text>
                );
            },
        },
        {
            title: t('eventStats.columns.count'),
            dataIndex: 'count',
            width: 120,
            align: 'right',
            sorter: (a, b) => a.count - b.count,
            defaultSortOrder: 'descend',
            render: (v) => <Tag color="purple">{v}</Tag>,
        },
    ];

    return (
        <Card
            title={
                <Space>
                    <BarChartOutlined />
                    {t('eventStats.title')}
                </Space>
            }
            extra={
                <Button icon={<ReloadOutlined />} onClick={fetchStats} loading={loading} size="small">
                    {t('common.refresh')}
                </Button>
            }
            style={{ marginBottom: 24 }}
        >
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={24} md={6}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Select
                            allowClear
                            mode="multiple"
                            placeholder={t('eventStats.filterEvent')}
                            value={eventNames}
                            onChange={(values) => setEventNames(Array.isArray(values) ? values : [])}
                            options={eventOptions}
                            style={{ flex: 1 }}
                            showSearch
                            optionFilterProp="label"
                            maxTagCount="responsive"
                        />
                        <Button
                            icon={<ClearOutlined />}
                            onClick={() => setEventNames([])}
                            disabled={eventNames.length === 0}
                            aria-label="Clear selected events"
                        />
                    </div>
                </Col>
                <Col xs={24} md={6}>
                    <Select
                        allowClear
                        placeholder={t('eventStats.filterProperty')}
                        value={propertyID}
                        onChange={setPropertyID}
                        options={propertyOptions}
                        style={{ width: '100%' }}
                        showSearch
                        optionFilterProp="label"
                    />
                </Col>
                <Col xs={24} md={6}>
                    <Select
                        allowClear
                        placeholder={t('eventStats.filterUrl')}
                        value={url}
                        onChange={(v) => setUrl(v || undefined)}
                        options={urlOptions}
                        style={{ width: '100%' }}
                        showSearch
                        optionFilterProp="label"
                    />
                </Col>
                <Col xs={24} md={6}>
                    <RangePicker
                        value={range}
                        onChange={(v) => setRange(v || [])}
                        style={{ width: '100%' }}
                        allowClear={false}
                    />
                </Col>
                <Col xs={24} md={6}>
                    <Select
                        value={groupBy}
                        onChange={setGroupBy}
                        options={[
                            { value: 'day', label: t('eventStats.groupDay') },
                            { value: 'hour', label: t('eventStats.groupHour') },
                        ]}
                        style={{ width: '100%' }}
                    />
                </Col>
            </Row>

            {loading && !data ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin />
                </div>
            ) : !data ? (
                <Empty description={t('eventStats.noData')} />
            ) : (
                <>
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                                    {t('eventStats.totalEvents')}
                                </Typography.Text>
                                <Typography.Title level={3} style={{ margin: 0 }}>
                                    {data.summary.totalCount.toLocaleString()}
                                </Typography.Title>
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                                    {t('eventStats.uniqueEvents')}
                                </Typography.Text>
                                <Typography.Title level={3} style={{ margin: 0 }}>
                                    {data.summary.uniqueEventNames}
                                </Typography.Title>
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                                    {t('eventStats.uniqueProperties')}
                                </Typography.Text>
                                <Typography.Title level={3} style={{ margin: 0 }}>
                                    {data.summary.uniqueProperties}
                                </Typography.Title>
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                                    {t('eventStats.uniqueUrls')}
                                </Typography.Text>
                                <Typography.Title level={3} style={{ margin: 0 }}>
                                    {data.summary.uniqueUrls ?? 0}
                                </Typography.Title>
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                            <Card size="small" title={t('eventStats.eventBreakdown')} style={{ height: '100%' }}>
                                <Table
                                    dataSource={data.eventBreakdown}
                                    columns={breakdownColumns}
                                    rowKey="_id"
                                    size="small"
                                    pagination={{ pageSize: 10, hideOnSinglePage: true }}
                                    locale={{ emptyText: t('eventStats.noEvents') }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card size="small" title={t('eventStats.propertyBreakdown')} style={{ height: '100%' }}>
                                <Table
                                    dataSource={data.propertyBreakdown}
                                    columns={propertyColumns}
                                    rowKey="_id"
                                    size="small"
                                    pagination={{ pageSize: 10, hideOnSinglePage: true }}
                                    locale={{ emptyText: t('eventStats.noEvents') }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Card size="small" title={t('eventStats.urlBreakdown')} style={{ marginTop: 16 }}>
                        <Table
                            dataSource={data.urlBreakdown || []}
                            columns={urlColumns}
                            rowKey="_id"
                            size="small"
                            pagination={{ pageSize: 10, hideOnSinglePage: true }}
                            locale={{ emptyText: t('eventStats.noEvents') }}
                        />
                    </Card>

                    <Card size="small" title={t('eventStats.timeSeries')} style={{ marginTop: 16 }}>
                        {data.timeSeries.length === 0 ? (
                            <Empty description={t('eventStats.noEvents')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {data.timeSeries.map(row => {
                                    const pct = maxTimeSeries > 0 ? (row.count / maxTimeSeries) * 100 : 0;
                                    return (
                                        <div key={row._id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <div style={{ width: 140, fontSize: 12, color: '#666' }}>
                                                {formatDateLabel(row._id, groupBy)}
                                            </div>
                                            <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, height: 18, overflow: 'hidden' }}>
                                                <div
                                                    style={{
                                                        width: `${pct}%`,
                                                        height: '100%',
                                                        background: 'linear-gradient(90deg, #5c7cfa, #4c6ef5)',
                                                    }}
                                                />
                                            </div>
                                            <div style={{ width: 60, textAlign: 'right', fontSize: 13, fontWeight: 500 }}>
                                                {row.count}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Card>
                </>
            )}
        </Card>
    );
};

export default EventStatsPanel;
